/**
 * Member Info to AssessmentApi Model Mapper
 *
 * Transforms member-info-like payloads to AssessmentApi (Conductor) models. Provides a single source of truth for model
 * transformations.
 */

import { AssessmentApi } from '../../api';
import { formatDateForApi } from '../../utils/formatters/date-formatters';
import type { FhirMemberInfoInput } from '../models/member-info-input.model';

/** Helper: Coerce value to number with fallback */
function coerceNumberOrDefault(value: any, fallback: number = 0): number {
  const num = Number(value);
  return isNaN(num) ? fallback : num;
}

/** Maps MemberInfo to FHIR Patient API model */
export function toMemberResponse(memberInfo: FhirMemberInfoInput): AssessmentApi.MemberResponse {
  const fullName = buildFullName(memberInfo);

  return new AssessmentApi.MemberResponse({
    memberId: coerceNumberOrDefault(memberInfo.memberId),
    name: fullName || undefined,
    gender: memberInfo.gender,
    dateOfBirth: toIsoDate(memberInfo.dateOfBirth),
    email: memberInfo.email,
    mailingAddress: memberInfo.address,
    primaryPhoneNumber: memberInfo.contactNumber,
    clientMemberId: memberInfo.clientMemberId,
    lineOfBusiness: memberInfo.lineOfBusiness,
  });
}

/** Maps MemberInfo to FHIR Practitioner API model */
export function toMemberPCPResponse(memberInfo: FhirMemberInfoInput): AssessmentApi.MemberPCPResponse {
  return new AssessmentApi.MemberPCPResponse({
    name: memberInfo.primaryCareProvider,
    npiNumber: memberInfo.primaryCareProviderNpiNumber,
    phoneNumber: memberInfo.primaryCareProviderPhoneNumber,
    faxNumber: memberInfo.primaryCareProviderFaxNumber,
    address: memberInfo.primaryCareProviderAddress,
  });
}

/** Maps MemberInfo to FHIR Appointment API model */
export function toMemberAppointmentResponse(memberInfo: FhirMemberInfoInput): AssessmentApi.MemberAppointmentResponse {
  const clientId = coerceNumberOrDefault(memberInfo.appointment?.clientId, coerceNumberOrDefault(memberInfo.client));
  const vendorId = coerceNumberOrDefault(memberInfo.appointment?.vendorId);

  return new AssessmentApi.MemberAppointmentResponse({
    appointmentDateTime: toIsoDateTime(memberInfo.encounterDate),
    scheduledNPI: memberInfo.primaryCareProviderNpiNumber,
    clientId,
    vendorId,
    memberId: coerceNumberOrDefault(memberInfo.memberId),
    appointmentConfirmationId: memberInfo.appointmentConfirmationId,
    incentiveAmount: memberInfo.incentiveAmount,
    incentiveType: memberInfo.incentiveType,
  });
}

/** Helper: Build full name from first and last name */
function buildFullName(memberInfo: FhirMemberInfoInput): string {
  return [memberInfo.firstName, memberInfo.lastName]
    .filter((part) => !!part)
    .join(' ')
    .trim();
}

/** Helper: Convert date/string to ISO date format (YYYY-MM-DD) Uses conductor's formatDateForApi utility */
function toIsoDate(value?: Date | string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  return formatDateForApi(value, 'YYYY-MM-DD');
}

/** Helper: Convert date/string to ISO datetime format (ISO 8601 full) */
function toIsoDateTime(value?: Date | string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value;
  }
  return value.toISOString();
}

export class MemberInfoToApiMapper {
  static toMemberResponse = toMemberResponse;
  static toMemberPCPResponse = toMemberPCPResponse;
  static toMemberAppointmentResponse = toMemberAppointmentResponse;
}
